import { apiRequest } from './api';

// Types
export interface ScheduleConfig {
  type: 'once' | 'daily' | 'weekly' | 'monthly';
  time: string;
  timezone: string;
  daysOfWeek?: number[];  // Array of 0-6 (Sun-Sat) for weekly - supports multiple days
  dayOfMonth?: number;
  startDate?: string;
  endDate?: string;
}

export interface EmailDigestConfig {
  sourceEmail: string;           // e.g., "creator@culturegenesis.com"
  recipients: string[];          // e.g., ["team@culturegenesis.com"]
  daysBack: number;              // Default 7
  includeAttachments: boolean;   // Default false
}

export interface ReportConfig {
  recipients: string[];          // @culturegenesis.com addresses
  topN: number;                  // Default 40
  includeAllDef: boolean;        // Default true
}

export interface IoReconciliationConfig {
  testEmail?: string;            // Override email recipient for testing
}

export interface SlackSummaryConfig {
  scope: 'mentions-and-dms' | 'specific-channels';
  channelIds?: string[];
  daysBack: number;
  customSubject?: string;
}

export interface SlackChannel {
  id: string;
  name: string;
  isPrivate: boolean;
  isMpim: boolean;
}

export interface ScheduledTask {
  id: string;
  userId: string;
  userEmail: string;
  userName: string;
  skillName: string;
  prompt: string;
  channelName?: string;
  schedule: ScheduleConfig;
  cloudSchedulerJobName?: string;
  status: 'active' | 'paused' | 'completed' | 'deleted';
  lastRunAt?: string;
  lastRunStatus?: 'success' | 'failed';
  nextRunAt?: string;
  runCount: number;
  failCount: number;
  createdAt: string;
  updatedAt: string;
  customSubject?: string;
  // Email Digest specific (optional, only for email-digest skill)
  emailDigestConfig?: EmailDigestConfig;
  // Slack Summary specific (optional, only for slack-summary skill)
  slackSummaryConfig?: SlackSummaryConfig;
}

export interface TaskExecution {
  id: string;
  taskId: string;
  userId: string;
  sessionId: string;
  messageId: string;
  status: 'running' | 'completed' | 'failed';
  skillName: string;
  prompt: string;
  result?: string;
  error?: string;
  startedAt: string;
  completedAt?: string;
  emailSentAt?: string;
  emailError?: string;
  emailFailedAt?: string;
  shareLinkId?: string;
  shareLinkUrl?: string;
}

export interface AvailableSkill {
  name: string;
  description: string;
}

// API Functions

/**
 * Get list of available skills for scheduling
 */
export const getAvailableSkills = async (): Promise<AvailableSkill[]> => {
  const response = await apiRequest('/api/scheduled-tasks/available-skills');
  return response.skills;
};

/**
 * Get all scheduled tasks for the current user
 */
export const getScheduledTasks = async (): Promise<ScheduledTask[]> => {
  const response = await apiRequest('/api/scheduled-tasks');
  return response.tasks;
};

/**
 * Get a specific scheduled task
 */
export const getScheduledTask = async (taskId: string): Promise<ScheduledTask> => {
  const response = await apiRequest(`/api/scheduled-tasks/${taskId}`);
  return response.task;
};

/**
 * Create a new scheduled task
 */
export const createScheduledTask = async (params: {
  skillName: string;
  prompt: string;
  schedule: ScheduleConfig;
  channelName?: string;
  emailDigestConfig?: EmailDigestConfig;
  reportConfig?: ReportConfig;
  customSubject?: string;
  ioReconciliationConfig?: IoReconciliationConfig;
  slackSummaryConfig?: SlackSummaryConfig;
}): Promise<{ taskId: string; cloudSchedulerJobName?: string; nextRunAt?: string }> => {
  const response = await apiRequest('/api/scheduled-tasks', {
    method: 'POST',
    body: JSON.stringify(params),
  });
  return response;
};

/**
 * Update a scheduled task
 */
export const updateScheduledTask = async (
  taskId: string,
  updates: {
    prompt?: string;
    schedule?: ScheduleConfig;
    channelName?: string;
    customSubject?: string | null;
  }
): Promise<void> => {
  await apiRequest(`/api/scheduled-tasks/${taskId}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
};

/**
 * Pause a scheduled task
 */
export const pauseScheduledTask = async (taskId: string): Promise<void> => {
  await apiRequest(`/api/scheduled-tasks/${taskId}/pause`, {
    method: 'PUT',
  });
};

/**
 * Resume a scheduled task
 */
export const resumeScheduledTask = async (taskId: string): Promise<void> => {
  await apiRequest(`/api/scheduled-tasks/${taskId}/resume`, {
    method: 'PUT',
  });
};

/**
 * Delete a scheduled task
 */
export const deleteScheduledTask = async (taskId: string): Promise<void> => {
  await apiRequest(`/api/scheduled-tasks/${taskId}`, {
    method: 'DELETE',
  });
};

/**
 * Get the Slack channels the current user is a member of. Requires the
 * user to have connected Slack (SlackConfig surfaces the Connect button).
 */
export const getSlackChannels = async (): Promise<SlackChannel[]> => {
  const response = await apiRequest('/api/scheduled-tasks/slack/channels');
  return response.channels || [];
};

/**
 * Get execution history for a task
 */
export const getTaskExecutions = async (
  taskId: string,
  limit: number = 20
): Promise<TaskExecution[]> => {
  const response = await apiRequest(`/api/scheduled-tasks/${taskId}/executions?limit=${limit}`);
  return response.executions;
};

// Helper functions

/**
 * Format schedule for display
 */
export const formatSchedule = (schedule: ScheduleConfig): string => {
  const timeStr = schedule.time;
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const fullDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  switch (schedule.type) {
    case 'once':
      return `Once at ${timeStr}`;
    case 'daily':
      return `Daily at ${timeStr}`;
    case 'weekly':
      const selectedDays = schedule.daysOfWeek || [0];
      if (selectedDays.length === 1) {
        return `Every ${fullDays[selectedDays[0]]} at ${timeStr}`;
      } else if (selectedDays.length === 7) {
        return `Daily at ${timeStr}`;
      } else if (selectedDays.length === 5 &&
                 selectedDays.includes(1) && selectedDays.includes(2) &&
                 selectedDays.includes(3) && selectedDays.includes(4) &&
                 selectedDays.includes(5)) {
        return `Weekdays at ${timeStr}`;
      } else if (selectedDays.length === 2 &&
                 selectedDays.includes(0) && selectedDays.includes(6)) {
        return `Weekends at ${timeStr}`;
      } else {
        const dayNames = selectedDays.sort((a, b) => a - b).map(d => days[d]).join(', ');
        return `Every ${dayNames} at ${timeStr}`;
      }
    case 'monthly':
      const suffix = getOrdinalSuffix(schedule.dayOfMonth || 1);
      return `Monthly on the ${schedule.dayOfMonth}${suffix} at ${timeStr}`;
    default:
      return schedule.type;
  }
};

/**
 * Get ordinal suffix (st, nd, rd, th)
 */
const getOrdinalSuffix = (n: number): string => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
};

